import { defineStore } from "pinia";

import axios from "../../axios-auth";
import qs from "qs";
import { i18n, PREFIX } from "../../i18n.js";
import { pathOr } from "ramda";
import hash from "object-hash";
import {
  callbackService,
  isRegisteredCallback,
  registerCallback
} from "../../helpers/callbackService";

import { useWebproductStore } from "@/stores/modules/webproduct";
import { useCheckedPageOptionsStore } from "@/stores/modules/checkedPageOptions";
import { useMyAccountStore } from "@/stores/modules/myAccount";
import { usePersonalizeStore } from "@/stores/modules/personalize";
import { useUploadsStore } from "@/stores/modules/uploads";
import { useCustomStore } from "@/stores/modules/custom";
import { useConfigStore } from "@/stores/modules/config";
import { useBundleStore } from "@/stores/modules/bundle";
import { useUrlsStore } from "@/stores/modules/urls";
import { usePreview3DStore } from "@/stores/modules/preview3D";
import { useQuantityFieldStore } from "@/stores/modules/quantityField";
import { usePreflightPitstopStore } from "@/stores/modules/preflightPitstop";
import { usePreflightDataStore } from "@/stores/modules/preflightData";
import { usePackingOptionsStore } from "@/stores/modules/packingOptions";
import { useImageEditorStore } from "@/stores/modules/imageEditor";
import { useUpdatesStore } from "@/stores/modules/updates";

export const useChangeOptionsStore = defineStore("changeoptions", {
  state: () => ({
    changeOptionsData: {},
    isChangeOptionsLoading: false,
    isPageOptionsLoading: false,
    addToCartCallbacks: [],
    matrixOneSelection: false,
    callChangeOptions: true,
    quantityCode: null,
    prevChangeOptionsData: null
  }),

  getters: {
    getSummarySelectedPageOptions(state) {
      const checkedPageOptionsStore = useCheckedPageOptionsStore();
      var summary = [];
      for (let pp in state.changeOptionsData.product_selection.productparts) {
        var ppObj = state.changeOptionsData.product_selection.productparts[pp];
        var data = {};
        for (let pageOptionKey in ppObj.prodpart_options) {
          if (
            checkedPageOptionsStore.getIsCheckedPageOptionActive &&
            !checkedPageOptionsStore.getCheckedInPP({
              po_name: pageOptionKey,
              pp: pp
            })
          ) {
            continue;
          }
          var item = ppObj.prodpart_options[pageOptionKey];
          if (
            pageOptionKey == "format" &&
            item.po_value.startsWith("Custom format")
          ) {
            data[i18n.t(PREFIX.PO + item.po_name)] = item.po_value.replace(
              "Custom format",
              i18n.t("Custom format")
            );
          } else {
            data[i18n.t(PREFIX.PO + item.po_name)] = i18n.t(
              PREFIX.POV + item.po_value
            );
          }
        }
        if (Object.keys(data).length != 0) {
          var summaryItem = {
            name: i18n.t(PREFIX.PP + ppObj.prodpart_name),
            data
          };

          summary.push(summaryItem);
        }
      }
      return summary;
    },
    getPrevChangeOptionsData(state) {
      return state.prevChangeOptionsData;
    },
    getPersonalizationCustomPrices(state) {
      var prices = {};
      for (var qty in state.changeOptionsData.price_table) {
        if (qty.includes("custom_qty_")) {
          if (
            state.changeOptionsData.price_table[qty].hasOwnProperty(
              "formatted_base_price"
            )
          ) {
            prices[state.changeOptionsData.price_table[qty].quantity] =
              state.changeOptionsData.price_table[qty].formatted_base_price;
          } else {
            prices[state.changeOptionsData.price_table[qty].quantity] = i18n.n(
              state.changeOptionsData.price_table[qty].base_price,
              "currency"
            );
          }
        }
      }

      return prices;
    },
    getQuantityCode(state) {
      return state.quantityCode;
    },
    getChangeOptionsTemplate(state) {
      var template = pathOr(0, ["changeOptionsData", "template"], state);
      if (template && template.length == 0) {
        return 0;
      }
      return template;
    },
    getChangeOptionsBundleTemplate(state) {
      var template = pathOr(0, ["changeOptionsData", "bundle_template"], state);
      if (template && template.length == 0) {
        return 0;
      }
      return template;
    },
    getCallChangeOptions(state) {
      return state.callChangeOptions;
    },
    getMatrixOneSelection(state) {
      const webproductStore = useWebproductStore();
      if (
        [
          "upload_without_configuration",
          "upload_configuration",
          "personalization_configuration",
          "upload_personalization_configuration",
          "upload_personalization_configuration_packaging"
        ].includes(webproductStore.getProductType)
      ) {
        return true;
      }

      if (webproductStore.getIsMobile) {
        if (
          [
            "upload_fullpage_configuration",
            "personalization_fullpage_configuration",
            "upload_personalization_fullpage_configuration"
          ].includes(webproductStore.getProductType)
        ) {
          return true;
        }
      }

      if (webproductStore.getIsSimpleConfigurationProduct) {
        return true;
      }
      return state.matrixOneSelection;
    },
    getPackingTdTexturesFromTemplateMapping(state) {
      return state.changeOptionsData.td_data
        ? state.changeOptionsData.td_data
        : null;
    },
    getSelectedOption(state) {
      return function (data) {
        const myAccountStore = useMyAccountStore();
        if (myAccountStore.getIsMyAccount) {
          return myAccountStore.getMyAccountSelection["PP_" + data.pp][
            data.option
          ];
        } else if (!state.callChangeOptions) {
          return "";
        }
        return state.changeOptionsData.pp_opt["PP_" + data.pp][data.option];
      };
    },
    getQuantity(state) {
      const webproductStore = useWebproductStore();
      const quantityFieldStore = useQuantityFieldStore();

      if (webproductStore.getPersonalizationQuantityWithPrice) {
        return quantityFieldStore.getQuantityField;
      }
      if (state.changeOptionsData.qty != undefined) {
        return state.changeOptionsData.qty;
      }
      return quantityFieldStore.getQuantityField;
    },
    getSummary(state) {
      var summary = [];
      for (let pp in state.changeOptionsData.product_selection.productparts) {
        var ppObj = state.changeOptionsData.product_selection.productparts[pp];
        var data = {};
        for (let pageOptionKey in ppObj.prodpart_options) {
          var item = ppObj.prodpart_options[pageOptionKey];

          let translation = i18n.t(PREFIX.POV + item.po_value);
          if (translation.startsWith(PREFIX.POV)) {
            translation = item.po_value;
          }
          data[i18n.t(PREFIX.PO + item.po_name)] = translation;
        }
        var summaryItem = {
          name: i18n.t(PREFIX.PP + ppObj.prodpart_name),
          data
        };

        summary.push(summaryItem);
      }
      return summary;
    },
    getPhotoMapping(state) {
      return state.changeOptionsData.photo
        ? state.changeOptionsData.photo
        : null;
    },
    getFactsheet(state) {
      return state.changeOptionsData.factsheet_files
        ? state.changeOptionsData.factsheet_files
        : null;
    },
    getIsChangeOptionsEmpty(state) {
      var no_qty = Object.keys(state.changeOptionsData).filter(el => {
        return el != "qty";
      });
      return no_qty.length == 0;
    },
    getIsPageOptionsLoading(state) {
      const webproductStore = useWebproductStore();
      return webproductStore.getEnableLoader
        ? state.isPageOptionsLoading
        : false;
    },
    getAddToCartCallbacks(state) {
      return state.addToCartCallbacks;
    },
    getChangeOptionsPostData(state) {
      const that = this;
      return function (makeCall) {
        const webproductStore = useWebproductStore();
        const bundleStore = useBundleStore();
        const preflightPitstopStore = usePreflightPitstopStore();
        const customStore = useCustomStore();
        const updatesStore = useUpdatesStore();
        if (that.getIsChangeOptionsEmpty) {
          return webproductStore.getInitialChangeOptionsPostData(makeCall);
        }

        var qties = customStore.getCustomQuantitiesLabels;

        let postData = {
          webproduct: webproductStore.getWebProductId,
          qty: state.changeOptionsData.qty,
          custom_quantities: qties.length == 0 ? "" : qties
        };
        if (webproductStore.getStoreCode) {
          postData["store_code"] = webproductStore.getStoreCode;
        }
        if (updatesStore.getAppSelectionId) {
          postData["selection"] = webproductStore.getSelectionId;
        }

        var data = state.changeOptionsData;

        for (let option in data.pp_opt) {
          if (option.startsWith("PP_")) {
            if (
              !bundleStore.getBundleIsActiveProductPart(
                option.replace("PP_", "")
              )
            ) {
              continue;
            }
            for (let attribute in data.pp_opt[option]) {
              postData["print_options[" + option + "][" + attribute + "][]"] =
                Array.isArray(data.pp_opt[option][attribute])
                  ? data.pp_opt[option][attribute][0]
                  : data.pp_opt[option][attribute];
            }
          } else if (option.startsWith("PP")) {
            const pp = bundleStore.getBundleMainProductPart
              ? bundleStore.getBundleMainProductPart
              : webproductStore.getActiveProductPartCode;
            postData["print_options[" + option + "][]"] = pp;
          } else {
            if (makeCall) {
              if (Array.isArray(data.pp_opt[option][option])) {
                if (data.pp_opt[option][option].length) {
                  postData["print_options[" + option + "][" + option + "]"] =
                    data.pp_opt[option][option];
                } else {
                  postData["print_options[" + option + "][" + option + "][]"] =
                    "";
                }
              } else {
                postData["print_options[" + option + "][" + option + "][]"] =
                  data.pp_opt[option][option];
              }
            } else {
              if (Array.isArray(data.pp_opt[option][option])) {
                if (data.pp_opt[option][option].length) {
                  for (var item in data.pp_opt[option][option]) {
                    postData[
                      "print_options[" +
                        option +
                        "][" +
                        option +
                        "][" +
                        item +
                        "]"
                    ] = data.pp_opt[option][option][item];
                  }
                } else {
                  postData["print_options[" + option + "][" + option + "][]"] =
                    "";
                }
              } else {
                postData["print_options[" + option + "][" + option + "][]"] =
                  data.pp_opt[option][option];
              }
            }
          }
        }

        if (makeCall) {
          postData = {
            ...postData,
            ...preflightPitstopStore.getPreflightSendData
          };
        }

        return postData;
      };
    },
    getChangeOptionsHiddenInputs(state) {
      const webproductStore = useWebproductStore();
      const packingOptionsStore = usePackingOptionsStore();
      const bundleStore = useBundleStore();
      var data = this.getChangeOptionsPostData(false);
      delete data.qty; // not needed

      // add new field for packing
      if (webproductStore.getIsPackingEnabled) {
        var packingData = packingOptionsStore.getPackingHiddenInputs;
        data = { ...data, ...packingData };
      }

      if (bundleStore.getBundleIsActive) {
        data["bundle_type"] = 1;
      }

      return data;
    },
    getChangeOptionsData(state) {
      return state.changeOptionsData;
    },
    getChangeOptionsDataPPOpt(state) {
      return state.changeOptionsData.pp_opt;
    },
    getQuantities(state) {
      var items = [];
      for (var qty in state.changeOptionsData.price_table) {
        var item = {
          code: qty,
          // code: state.changeOptionsData.price_table[qty].quantity,
          ...state.changeOptionsData.price_table[qty]
        };

        if (
          state.changeOptionsData.qty ==
          state.changeOptionsData.price_table[qty].quantity
        ) {
          item["active"] = true;
        }

        items.push(item);
      }

      items.sort(function (a, b) {
        var inta = parseInt(a.quantity);
        var intb = parseInt(b.quantity);
        return inta == intb ? 0 : inta > intb ? 1 : -1;
      });

      return items;
    },
    getHasGrossPrice(state) {
      for (const qty of Object.keys(state.changeOptionsData.price_table)) {
        if (state.changeOptionsData.price_table[qty].gross_price) {
          return true;
        }
        break;
      }
      return false;
    },
    getQuantitiesValues(state) {
      var items = [];
      for (var qty in state.changeOptionsData.price_table) {
        items.push(state.changeOptionsData.price_table[qty].quantity);
      }
      return items;
    },
    getQuantitiesValuesWithCode(state) {
      var items = [];
      for (var qty in state.changeOptionsData.price_table) {
        items.push({
          qtyCode: qty,
          qty: state.changeOptionsData.price_table[qty].quantity
        });
      }
      return items;
    },
    getDenyRules(state) {
      return state.changeOptionsData.deny_rules;
    },
    getInformation(state) {
      return state.changeOptionsData.information;
    },
    getShowChangeOptionsLoader(state) {
      const webproductStore = useWebproductStore();
      return webproductStore.getEnableLoader
        ? state.isChangeOptionsLoading
        : false;
    },
    getIsChangeOptionsLoading(state) {
      return state.isChangeOptionsLoading;
    }
  },

  actions: {
    setMatrixOneSelection(payload) {
      this.matrixOneSelection = payload;
    },
    setIsChangeOptionsLoading(payload) {
      this.isChangeOptionsLoading = payload;
    },

    setPageOptionMutation(payload) {
      const webproductStore = useWebproductStore();
      const bundleStore = useBundleStore();
      const checkedPageOptionsStore = useCheckedPageOptionsStore();
      const configStore = useConfigStore();
      let activePageOption = webproductStore.getActivePageOptions;
      let activeProductPartCode = webproductStore.getActiveProductPartCode;
      if (payload.option) {
        activePageOption = payload.option;
      }
      if (payload.ppcode) {
        activeProductPartCode = payload.ppcode;
      }

      if (
        isRegisteredCallback(
          callbackService.callbacks.afterChangePageOptionCallback
        )
      ) {
        this.callAfterChangePageOptionCallback({
          value: payload.value,
          option: activePageOption,
          type: "main",
          ppcode: activeProductPartCode
        });
      }
      // array values set ???
      var changeOptionsDataPPOpt = { ...this.getChangeOptionsDataPPOpt };
      changeOptionsDataPPOpt["PP_" + activeProductPartCode][activePageOption] =
        [payload.value];
      this.changeOptionsData.pp_opt = changeOptionsDataPPOpt;
      //moveToNextPageOption
    },
    moveToNextPageOption(payload) {
      const webproductStore = useWebproductStore();
      const bundleStore = useBundleStore();
      const checkedPageOptionsStore = useCheckedPageOptionsStore();
      const configStore = useConfigStore();
      let activePageOption = webproductStore.getActivePageOptions;
      let activeProductPartCode = webproductStore.getActiveProductPartCode;
      if (payload.option) {
        activePageOption = payload.option;
      }
      if (payload.ppcode) {
        activeProductPartCode = payload.ppcode;
      }

      if (payload && payload.layout) {
        // move to the next item
        var activePageOptions = { ...webproductStore.getAllActivePageOptions };
        var activeProductPageOptionsKyes = Object.keys(
          webproductStore.getProductPageOptionsForCode({
            code: activeProductPartCode,
            all: false
          })
        );
        var index = activeProductPageOptionsKyes.indexOf(activePageOption);
        if (index > -1) {
          const checkedData = {
            pp: activeProductPartCode,
            po_index: index
          };
          checkedPageOptionsStore.setCheckedPOItem(checkedData);

          if (index != activeProductPageOptionsKyes.length - 1) {
            webproductStore.setActiveProductPageOption({
              ppcode: activeProductPartCode,
              option: activeProductPageOptionsKyes[++index]
            });
          } else {
            if (bundleStore.getBundleIsActive) {
              bundleStore.setBundleMatrixComplete(activeProductPartCode);
              return;
            }
            // move to the next PP if it exists
            var productPartsCodesKeys = webproductStore.getProductPartsCodes;
            var indexPP = productPartsCodesKeys.indexOf(
              webproductStore.getActiveProductPartCode
            );
            if (indexPP != -1) {
              if (indexPP != productPartsCodesKeys.length - 1) {
                webproductStore.setActiveProductPartName(
                  productPartsCodesKeys[++indexPP]
                );

                webproductStore.setActiveProductPageOption({
                  ppcode: activeProductPartCode,
                  option: Object.keys(
                    webproductStore.getProductPageOptionsForCode({
                      code: activeProductPartCode,
                      all: false
                    })
                  )[0]
                });
              } else {
                // finalized selection for all PP
                configStore.setMatrixComplete(true);
              }
            }
          }
        }
      }
    },
    setOtherOption(payload) {
      var changeOptionsDataPPOpt = { ...this.getChangeOptionsDataPPOpt };
      changeOptionsDataPPOpt[payload.option][payload.option] = Array.isArray(
        payload.value
      )
        ? payload.value
        : [payload.value];
      this.changeOptionsData.pp_opt = changeOptionsDataPPOpt;

      if (
        isRegisteredCallback(
          callbackService.callbacks.afterChangePageOptionCallback
        )
      ) {
        this.callAfterChangePageOptionCallback({
          value: payload.value,
          option: payload.option,
          type: "other"
        });
      }
    },
    setChangeOptionsData(responseData) {
      const bundleStore = useBundleStore();
      if (bundleStore.getBundleIsActive && this.changeOptionsData.pp_opt) {
        // we will not receive input for the PP that are not active. but we would still need them in this structure (pp_opt)
        responseData.pp_opt = {
          ...this.changeOptionsData.pp_opt,
          ...responseData.pp_opt
        };
      }
      this.changeOptionsData = responseData;
    },
    setQuantity(payload) {
      this.changeOptionsData = {
        ...this.changeOptionsData,
        qty: payload.qty
      };
      this.quantityCode = payload.qtyCode;

      this.changeOptions({});
    },
    registerAddToCartCallback(payload) {
      if (typeof payload === "function")
        this.addToCartCallbacks = [...this.addToCartCallbacks, payload];
    },
    setIsPageOptionsLoading(payload) {
      this.isPageOptionsLoading = payload;
    },
    setCallChangeOptions(payload) {
      this.callChangeOptions = payload;
    },
    setPrevChangeOptionsData(payload) {
      this.prevChangeOptionsData = payload;
    },
    setPageOptionFormat(payload) {
      this.setPageOptionMutation(payload);
    },
    setPageOption(payload) {
      const webproductStore = useWebproductStore();
      const packingOptionsStore = usePackingOptionsStore();
      this.setIsPageOptionsLoading(true);
      this.setMatrixOneSelection(true);

      if (payload.optionType == "main") {
        if (payload.option == webproductStore.getProductPartsOptionCode) {
          webproductStore.setActiveProductPartName(payload.value);
          this.setIsPageOptionsLoading(false);
          return;
        } else {
          this.setPageOptionMutation(payload);
          if (webproductStore.getIsPackingEnabled) {
            packingOptionsStore.setPackingOption(payload);
            if (
              packingOptionsStore.getRefreshGetDataPP.includes(payload.option)
            ) {
              packingOptionsStore.packingGetData({ callChangeOptions: false });
            }
          }
        }
      } else {
        // other option
        this.setOtherOption(payload);
      }
      if (payload.optionType !== "quantity") {
        payload.pageOptionPayload = {
          ...payload
        };
        this.changeOptions(payload);
      }
    },
    changeOptions(payload) {
      const webproductStore = useWebproductStore();
      const packingOptionsStore = usePackingOptionsStore();
      const preview3DStore = usePreview3DStore();
      const urlsStore = useUrlsStore();
      const imageEditorStore = useImageEditorStore();
      const preflightDataStore = usePreflightDataStore();
      if (
        !this.getCallChangeOptions ||
        !webproductStore.getCallChangeOptionPerType
      ) {
        if (payload.initialLoading) {
          webproductStore.setIsLoading(false);
          webproductStore.setHideInitialLoading(false);
          this.callInitialLoadingFinalized({
            type: "changeOptions",
            status: 1
          });
        }
        return;
      }

      let oldPOValueNeededForGetData = null;
      if (
        payload.initialLoading &&
        webproductStore.getHasInitialChangeOptionsResponse
      ) {
        const responseData = webproductStore.getHasInitialChangeOptionsResponse;
        callbackService.sendCallback(
          callbackService.callbacks.afterChangeOptionsCallback,
          responseData
        );

        this.handleChangeOptionsResponse({
          responseData,
          payload,
          oldPOValueNeededForGetData
        });
        return;
      }

      this.setIsChangeOptionsLoading(true);
      var changeOptionsPostData = this.getChangeOptionsPostData(true);
      if (this.getQuantityCode) {
        if (this.getIsChangeOptionsEmpty)
          changeOptionsPostData["print_options[quantity][quantity][]"] =
            this.getQuantityCode;
        else
          changeOptionsPostData["print_options[quantity][quantity]"] = [
            this.getQuantityCode
          ];
      }

      if (webproductStore.getIsPackingEnabled) {
        oldPOValueNeededForGetData =
          packingOptionsStore.getPOValueNeededForGetData;
      }

      //cache changeOptions call
      if (hash(changeOptionsPostData) == this.getPrevChangeOptionsData) {
        // same hash ... no need to call again changeOptions
        this.setIsChangeOptionsLoading(false);
        this.setIsPageOptionsLoading(false);
        if (payload.pageOptionPayload) {
          this.moveToNextPageOption({ ...payload.pageOptionPayload });
        }
        return;
      }

      this.setPrevChangeOptionsData(hash(changeOptionsPostData));

      if (
        isRegisteredCallback(
          callbackService.callbacks.beforeChangeOptionsCallback
        )
      ) {
        var changeOptionsPostDataResponse = callbackService.sendCallback(
          callbackService.callbacks.beforeChangeOptionsCallback,
          changeOptionsPostData
        );
        if (changeOptionsPostDataResponse) {
          changeOptionsPostData = changeOptionsPostDataResponse;
        }
      }
      axios.instance
        .post(
          urlsStore.getUrl(axios.apiChangeOption),
          qs.stringify(changeOptionsPostData)
        )
        .then(response => {
          callbackService.sendCallback(
            callbackService.callbacks.afterChangeOptionsCallback,
            response.data
          );

          if (!response.data.success) {
            console.log("Change options error : " + response.data.message);
            throw new Error("Could not get prices for selection");
          }
          var responseData = response.data;
          if (
            response.data.hasOwnProperty("success") &&
            response.data.hasOwnProperty("data")
          ) {
            responseData = response.data.data;
          }
          this.handleChangeOptionsResponse({
            responseData,
            payload,
            oldPOValueNeededForGetData
          });
        })
        .catch(error => {
          console.log(error);
          if (payload.initialLoading) {
            webproductStore.setNetworkError(error);
            this.callInitialLoadingFinalized({
              type: "changeOptions",
              status: 0
            });
          }
          this.setIsChangeOptionsLoading(false);
          webproductStore.setHideInitialLoading(false);
          this.setIsPageOptionsLoading(false);
        });
    },
    callAfterChangePageOptionCallback(payload) {
      const webproductStore = useWebproductStore();
      const customStore = useCustomStore();
      if (payload.type == "main") {
        const ppCode = payload.ppcode
          ? payload.ppcode
          : webproductStore.getActiveProductPartCode;
        const allPPOptions = webproductStore.getProductPageOptionsForCode({
          code: ppCode,
          all: true
        });
        if (allPPOptions[payload.option]) {
          const items = allPPOptions[payload.option].values.filter(el => {
            return el.value == payload.value;
          });
          if (items.length > 0) {
            callbackService.sendCallback(
              callbackService.callbacks.afterChangePageOptionCallback,
              {
                ...items[0],
                pp: ppCode,
                option: payload.option,
                multiple: allPPOptions[payload.option].type
              }
            );
          } else {
            if (
              payload.option == "format" &&
              payload.value.startsWith("custom_")
            ) {
              const itemsFormat = customStore.getCustomFormats.filter(el => {
                return el.value == payload.value;
              });
              if (itemsFormat.length > 0) {
                callbackService.sendCallback(
                  callbackService.callbacks.afterChangePageOptionCallback,
                  {
                    attributes: {
                      width: "" + itemsFormat[0].width,
                      height: "" + itemsFormat[0].height
                    },
                    label: itemsFormat[0].label,
                    value: payload.value,
                    pp: ppCode,
                    option: payload.option,
                    multiple: allPPOptions[payload.option].type
                  }
                );
              }
            }
          }
        }
      } else if (payload.type == "other") {
        const options = webproductStore.getWebProductData.options;
        if (options[payload.option]) {
          let items = options[payload.option].values.filter(el => {
            if (options[payload.option].type == "multiple") {
              return payload.value.includes(el.value);
            } else {
              return el.value == payload.value;
            }
          });
          if (options[payload.option].type == "multiple") {
            var newItems = {};
            for (var idx in items) {
              newItems[items[idx].value] = { ...items[idx] };
            }
            items = [{ attributes: newItems }];
          }

          if (items.length > 0) {
            callbackService.sendCallback(
              callbackService.callbacks.afterChangePageOptionCallback,
              {
                ...items[0],
                pp: null,
                option: payload.option,
                value: payload.value,
                multiple: options[payload.option].type
              }
            );
          }
        }
      }
    },
    callInitialLoadingFinalized(payload) {
      if (payload.type == "changeOptions" && payload.status == 1) {
        const configStore = useConfigStore();
        configStore.setListenScroll(true);
      }

      callbackService.sendCallback(
        callbackService.callbacks.initFinalizeCallback,
        payload
      );
    },
    handleChangeOptionsResponse({
      responseData,
      payload,
      oldPOValueNeededForGetData
    }) {
      const webproductStore = useWebproductStore();
      const packingOptionsStore = usePackingOptionsStore();
      const preview3DStore = usePreview3DStore();
      const urlsStore = useUrlsStore();
      const imageEditorStore = useImageEditorStore();
      const preflightDataStore = usePreflightDataStore();

      this.setChangeOptionsData(responseData);

      if (webproductStore.getIsPackagingAttrUpdateEnabled) {
        packingOptionsStore.updatePackagingAttributes(responseData);
        // dispatch("updatePackagingAttributes", responseData);
      }

      if (payload.initialLoading) {
        if (webproductStore.getIsPackingEnabled) {
          preview3DStore.setPreview3DLoading(true);
          packingOptionsStore.loadInitialPackingOptions(payload);
          packingOptionsStore.packingGetData({
            callChangeOptions: true,
            setLoading: true
          });
        } else {
          webproductStore.setIsLoading(false);
        }
        this.callInitialLoadingFinalized({
          type: "changeOptions",
          status: 1
        });
      }
      if (webproductStore.getIsLfp) {
        imageEditorStore.parseChangeOptionResponse(responseData);
      }
      if (responseData.preflight_check_updated) {
        preflightDataStore.setPreflightCheckData(
          responseData.preflight_check_updated
        );
      }

      if (this.getPhotoMapping) {
        callbackService.sendCallback(
          callbackService.callbacks.photoMappingCallback,
          this.getPhotoMapping
        );
      }

      if (responseData.template) {
        webproductStore.setChangeOptionsTemplate(responseData.template);
      }

      if (
        webproductStore.getIsPackingEnabled &&
        JSON.stringify(preview3DStore.getCurrentTdData) !=
          JSON.stringify(preview3DStore.getTdTexture)
      ) {
        preview3DStore.setTdImage();
      }

      if (webproductStore.getIsPackingEnabled && !payload.initialLoading) {
        // compare page options required by getDat with the new ones
        // call callChangeOptions false
        const allPageOptions = webproductStore.getAllProductPageOptions(true);
        let getDataCall = false;
        for (let refreshPo in packingOptionsStore.getRefreshGetDataPP) {
          const refreshItem =
            packingOptionsStore.getRefreshGetDataPP[refreshPo];
          if (
            oldPOValueNeededForGetData[refreshItem] !=
            allPageOptions[refreshItem].selected[0]
          ) {
            packingOptionsStore.setPackingOption({
              value: allPageOptions[refreshItem].selected[0],
              layout: false,
              option: refreshItem,
              optionType: "main"
            });
            getDataCall = true;
          }
        }
        if (getDataCall) {
          webproductStore
            .getSweetAlert({
              text: i18n.t(
                "Paper was changed. You will need to upload the artwork again!"
              ),
              icon: "warning",
              showCancelButton: false,
              showConfirmButton: true,
              confirmButtonText: i18n.t("Ok")
            })
            .then(userAction => {
              if (userAction.isConfirmed) {
              }
            });
          packingOptionsStore.packingGetData({ callChangeOptions: false });
        }
      }

      if (payload.pageOptionPayload) {
        this.moveToNextPageOption({ ...payload.pageOptionPayload });
      }
      this.setIsChangeOptionsLoading(false);
      webproductStore.setHideInitialLoading(false);
      this.setIsPageOptionsLoading(false);
    }
  }
});
